import SoundManager from "../../../../../lib/html5-software-library/src/ranj/audio/SoundManager";
import Button from "../../../../../lib/html5-software-library/src/ranj/components/buttons/Button";
import VerticalLayout from "../../../../../lib/html5-software-library/src/ranj/components/layouts/VerticalLayout";
import Slice9Button from "../../../../../lib/html5-software-library/src/ranj/components/scale9/Slice9Button";
import Content from "../../../../../lib/html5-software-library/src/ranj/eng/content/Content";
import ContentTypes from "../../../../../lib/html5-software-library/src/ranj/eng/content/ContentTypes";
import ConversationTopicContent from "../../../../../lib/html5-software-library/src/ranj/eng/content/dialog/ConversationTopicContent";
import ImageContent from "../../../../../lib/html5-software-library/src/ranj/eng/content/ImageContent";
import TextContent from "../../../../../lib/html5-software-library/src/ranj/eng/content/TextContent";
import RevisableLanguagePack from "../../../../../lib/html5-software-library/src/ranj/language/RevisableLanguagePack";
import Anchor from "../../../../../lib/html5-software-library/src/ranj/layout/Anchor";
import LayoutManager from "../../../../../lib/html5-software-library/src/ranj/layout/LayoutManager";
import MediatorManager from "../../../core/application/MediatorManager";
import AssetLibrary from "../../../core/assetloading/AssetLibrary";
import Config from "../../../core/config/Config";
import SoundType from "../../../core/sound/util/SoundType";
import AbstractMediator from "../../../core/ui/AbstractMediator";
import SkinKeyRegistry from "../../../core/ui/SkinKeyRegistry";
import NGTDialogPlayer from "../../../ngt/dialog/NGTDialogPlayer";
import NGTDialogPlayerEvent from "../../../ngt/dialog/NGTDialogPlayerEvent";
import CharacterTrackerEvent from "../../characters/event/CharacterTrackerEvent";
import CharacterModel from "../../characters/model/CharacterModel";
import DebugModel from "../../debug/model/DebugModel";
import MissionBriefingEvent from "../../mission-briefing/model/MissionBriefingEvent";
import { OnboardingStep } from "../../onboarding/model/OnboardingStep";
import PlayerModel from "../../player/model/PlayerModel";
import SessionModel from "../../session/model/SessionModel";
import DialogUtil from "../../util/DialogUtil";
import DisplayObjectUtil from "../../util/DisplayObjectUtil";
import PromiseUtil from "../../util/PromiseUtil";
import ConversationBriefingRecapEvent from "../event/ConversationBriefingRecapEvent";
import ConversationEndedEvent from "../event/ConversationEndedEvent";
import ConversationFinishedEvent from "../event/ConversationFinishedEvent";
import ConversationHintEvent from "../event/ConversationHintEvent";
import ConversationPhaseReasoningEvent from "../event/ConversationPhaseReasoningEvent";
import ConversationRollbackEvent from "../event/ConversationRollbackEvent";
import ConversationStartedEvent from "../event/ConversationStartedEvent";
import ConversationStateChangedEvent from "../event/ConversationStateChangedEvent";
import NewConversationMessageEvent from "../event/NewConversationMessageEvent";
import Conversation from "../model/Conversation";
import { GameMessageSender } from "../model/GameConversationMessage";
import CharacterSpeechMediator from "./CharacterSpeechMediator";
import ConversationCoachPanelMediator from "./ConversationCoachPanelMediator";
import ConversationHintMediator from "./ConversationHintMediator";
import ConversationInputPanelMediator from "./ConversationInputPanelMediator";
import PlayerSpeechMediator from "./PlayerSpeechMediator";

export default class ConversationScreenMediator extends AbstractMediator {
    /**
     * @constructor
     * @param {createjs.MovieClip} target 
     * @param {soma.EventDispatcher} dispatcher 
     * @param {LayoutManager} layoutManager 
     * @param {RevisableLanguagePack} languagePack 
     * @param {AssetLibrary} assetLibrary 
     * @param {MediatorManager} mediatorManager 
     * @param {SoundManager} soundManager 
     * @param {Config} config 
     * @param {SessionModel} sessionModel 
     * @param {PlayerModel} playerModel 
     */
    constructor(target, dispatcher, layoutManager, languagePack, assetLibrary, mediatorManager, soundManager, config, sessionModel, playerModel) {
        super(target, dispatcher, layoutManager);

        this._languagePack = languagePack;
        this._assetLibrary = assetLibrary;
        this._mediatorManager = mediatorManager;
        this._soundManager = soundManager;
        this._config = config;
        this._sessionModel = sessionModel;
        this._playerModel = playerModel;

        /** @type {NGTDialogPlayer} */
        this._dialogPlayer = this.getCustomData().dialogPlayer;
        /** @type {CharacterModel[]} */
        this._characters = this.getCustomData().characters;

        /** @type {Array<CharacterSpeechMediator>} */
        this._characterSpeechMediators = [];
        /** @type {Map<string, CharacterSpeechMediator>} */
        this._characterToSpeechMediatorMap = new Map();

        this._handleDialogContentBound = this._handleDialogContent.bind(this);
        this._handleCommandSequenceEndBound = this._handleCommandSequenceEnd.bind(this);

        this._workingMessageOffsetForScriptedContent = 0;
    }

    initialize() {
        super.initialize();

        this._target.bgImageContainer.removeAllChildren();
        this._initializeCharacterSpeechMediators();

        if (DebugModel.debugContent) {
            this._showDebug();
        }

        /** @type {PlayerSpeechMediator} */
        this._playerSpeechMediator = this._mediatorManager.createMediator(this._target.playerSpeechPanel, SkinKeyRegistry.PLAYER_SPEECH_COMPONENT);
        this._playerSpeechMediator.hideInstant();

        /** @type {ConversationHintMediator} */
        this._conversationHintMediator = this._mediatorManager.createMediator(this._target.coachHintPanel, SkinKeyRegistry.CONVERSATION_HINT_COMPONENT);
        this._conversationHintMediator.hideInstant();

        /** @type {ConversationCoachPanelMediator} */
        this._coachPanelMediator = this._mediatorManager.createMediator(this._target.coachPanel, SkinKeyRegistry.CONVERSATION_COACH_PANEL_COMPONENT);
        this._coachPanelMediator.hideInstant();

        /** @type {ConversationInputPanelMediator} */
        this._inputPanel = this._mediatorManager.createMediator(this._target.inputPanel, SkinKeyRegistry.CONVERSATION_INPUT_PANEL_COMPONENT);
        this._inputPanel.eventDispatcher.addEventListener(ConversationInputPanelMediator.PLAYER_INPUT_EVENT, this._submitPlayerInput.bind(this));
        this._inputPanel.eventDispatcher.addEventListener(ConversationInputPanelMediator.PLAYER_TYPE_EVENT, this._handlePlayerTyping.bind(this));
        this._inputPanel.hideInstant();

        this._backButton = this.createSlice9Button(this._target.backButton, this._handleBackButton.bind(this), this._languagePack.getText(`conversationScreen.backButtonLabel`), SoundType.BUTTON_CLICK, Slice9Button.HORIZONTAL);
        this._backButton.setVisible(false); // Hide for now until implemented.
        this._nextButton = this.createSlice9Button(this._target.nextButton, this._handleNextButton.bind(this), this._languagePack.getText(`conversationScreen.continueButtonLabel`), SoundType.BUTTON_CLICK, Slice9Button.HORIZONTAL);
        this._nextButton.setVisible(false);
        this._endButton = this.createSlice9Button(this._target.endButton, this._handleEndButton.bind(this), this._languagePack.getText(`conversationScreen.endButtonLabel`), SoundType.BUTTON_CLICK, Slice9Button.HORIZONTAL);
        this._endButton.setVisible(false);

        this._coachButton = this.createButton(this._target.coachButton, this._handleCoachButton.bind(this), '', SoundType.BUTTON_CLICK);
        this._coachButton.setVisible(false);
        // this._attachAudioToAttractState(this._coachButton, SoundType.COACH_BUTTON_NUDGE_REPEAT); // Enable to play repeating audio on attract state.

        this.registerEventListener(NewConversationMessageEvent.TYPE, this._handleNewConversationMessage.bind(this));
        this.registerEventListener(ConversationStartedEvent.TYPE, this._handleConversationStarted.bind(this));
        this.registerEventListener(ConversationEndedEvent.TYPE, this._handleConversationEnded.bind(this));
        this.registerEventListener(ConversationStateChangedEvent.TYPE, this._handleConversationStateChanged.bind(this));
        this.registerEventListener(ConversationHintEvent.PERFORM, this._handleConversationHint.bind(this));
        this.registerEventListener(ConversationBriefingRecapEvent.PERFORM, this._handleConversationBriefingRecap.bind(this));
        this.registerEventListener(CharacterTrackerEvent.CHANGED, this._handleCharacterTrackerChanged.bind(this));
        this.registerEventListener(ConversationPhaseReasoningEvent.PERFORM, this._handleConversationPhaseReasoning.bind(this));
        this.registerEventListener(ConversationRollbackEvent.PERFORM, this._handleConversationRollback.bind(this));

        this._addDialogPlayerListeners();

        this._target.bgImageContainer.addEventListener("click", this._handleBackgroundClicked.bind(this));

        this._target.sessionIdTextField.text = `Session: ${this._sessionModel.shortSessionId}`;

        this._start();
    }

    destroy() {
        super.destroy();


        this._removeDialogPlayerListeners();

        this._playerSpeechMediator.destroy();
        this._coachPanelMediator.destroy();
        this._inputPanel.destroy();

        for (const characterSpeechMediator of this._characterSpeechMediators) {
            characterSpeechMediator.destroy();
        }
        this._characterSpeechMediators = null;
        this._characterToSpeechMediatorMap = null; // Contains the same mediators as the array above, so no need to destroy them again.

        this._target.bgImageContainer.removeAllEventListeners();
    }

    _addDialogPlayerListeners() {
        this._dispatcher.addEventListener(NGTDialogPlayerEvent.DIALOG_CONTENT, this._handleDialogContentBound);
        this._dispatcher.addEventListener(NGTDialogPlayerEvent.COMMAND_SEQUENCE_END, this._handleCommandSequenceEndBound);
    }

    _removeDialogPlayerListeners() {
        this._dispatcher.removeEventListener(NGTDialogPlayerEvent.DIALOG_CONTENT, this._handleDialogContentBound);
        this._dispatcher.addEventListener(NGTDialogPlayerEvent.COMMAND_SEQUENCE_END, this._handleCommandSequenceEndBound);
    }

    _initializeCharacterSpeechMediators() {
        const characterSpeechSkins = [
            this._target.responseField1,
            this._target.responseField2,
        ];

        for (const characterSpeechSkin of characterSpeechSkins) {
            /** @type {CharacterSpeechMediator} */
            const characterSpeechMediator = this._mediatorManager.createMediator(characterSpeechSkin, SkinKeyRegistry.CHARACTER_SPEECH_COMPONENT);
            this._characterSpeechMediators.push(characterSpeechMediator);
        }

        for (const [index, character] of this._characters.entries()) {
            const characterSpeechMediator = this._characterSpeechMediators[index];
            const characterId = character.id.trim().toLowerCase();
            this._characterToSpeechMediatorMap.set(characterId, characterSpeechMediator);

            characterSpeechMediator.setImage(character.avatarImageId);
            characterSpeechMediator.setName(character.name);

            characterSpeechMediator.hideAllInstant();
        }
    }

    /**
     * Triggers when messages are added to the conversation.
     * NOTE: these can be character (AI) AND player messages!
     * @param {*} evt 
     * @returns 
     */
    async _handleNewConversationMessage(evt) {
        /** @type {NewConversationMessageEvent} */
        const newConversationMessagesEvent = evt.params.data;

        console.log(`[ConversationScreenMediator] New conversation messages:`, JSON.stringify(newConversationMessagesEvent.messages, null, 2));

        const characterMessages = newConversationMessagesEvent.messages.filter(message => message.sender !== GameMessageSender.PLAYER);

        // Stop loading animations for all characters. Only if the characters have messages to show.
        if (characterMessages.length > 0) {
            for (const characterSpeechMediator of this._characterSpeechMediators) {
                characterSpeechMediator.stopLoadingAnimation();
            }

            await this._playerSpeechMediator.hide();
        }

        // Handle character messages.
        for (const [index, message] of characterMessages.entries()) {
            const characterId = message.sender.trim().toLowerCase();
            const characterSpeechMediator = this._characterToSpeechMediatorMap.get(characterId);

            characterSpeechMediator.setAction(message.action);
            characterSpeechMediator.setSpeech(message.dialogue);

            characterSpeechMediator.setOffsetPosY(ConversationScreenMediator.CHARACTER_SPEECH_OFFSET_FOR_ORDERING * index);
            await characterSpeechMediator.showProfilePicture(0, true);
            await characterSpeechMediator.showPanels();

            const delay = DebugModel.fasterAnimations ? 0 : 650;
            await PromiseUtil.delay(delay); // Add a bit of delay before showing the next message.
        }

        // Show tracker updates.
        for (const characterSpeechMediator of this._characterSpeechMediators) {
            characterSpeechMediator.showCharacterTrackerFeedbacks();
            characterSpeechMediator.clearCharacterTrackerFeedbacks(); // Don't show it again next turn if it didn't change.
        }

        if (this._dialogPlayer.llmConversationPlayer.conversation.hasEnded) {
            this._endButton.setVisible(true);
            return;
        }

        const charactersTalked = characterMessages.length > 0;
        if (charactersTalked) { // Don't re-enable UI after player spoke.
            this._coachButton.setEnabled(true);
            await this.showAndAwaitOnboarding(OnboardingStep.CONVERSATION_HAND_OFF);

            this._enablePlayerInput();
            this._queueCoachButtonNudge();
        }
    }

    async _handleConversationStateChanged(evt) {
        /** @type {ConversationStateChangedEvent} */
        const conversationStateChangedEvent = evt.params.data;
        /** @type {Conversation} */
        const conversation = conversationStateChangedEvent.conversation;

        if (DebugModel.debugContent) {
            this._debugPhaseTextField.text = `Phase: ${conversation.currentPhaseId}`;
            this._debugOutcomeTextField.text = `Outcome: ${conversation.outcome}`;
        }
    }

    async _handleConversationHint(evt) {
        /** @type {ConversationHintEvent} */
        const conversationHintEvent = evt.params.data;
        const hint = conversationHintEvent.hint;

        await this._coachPanelMediator.hide();
        this._coachButton.setSelected(false);
        this._conversationHintMediator.showHint(hint);
    }

    async _handleConversationBriefingRecap(evt) {
        this._inputPanel.blurInputField();

        await this._coachPanelMediator.hide();
        this._coachButton.setSelected(false);

        this._dispatcher.dispatch(MissionBriefingEvent.START);
    }

    async _handleCharacterTrackerChanged(evt) {
        /** @type {CharacterTrackerEvent} */
        const data = evt.params.data;
        const character = data.character;
        const characterId = character.id.trim().toLowerCase();

        const characterSpeechMediator = this._characterToSpeechMediatorMap.get(characterId);
        characterSpeechMediator.addCharacterTrackerFeedback(data.trackerId, data.oldValue, data.newValue);
    }

    _handleConversationPhaseReasoning(evt) {
        /** @type {ConversationPhaseReasoningEvent} */
        const conversationPhaseReasoningEvent = evt.params.data;
        const reasoning = conversationPhaseReasoningEvent.reasoning;

        if (DebugModel.debugContent) {
            this._debugPhaseReasoningTextField.text = `PHASE REASONING: ${reasoning}`;
            console.log(`[ConversationScreenMediator] Phase reasoning: ${reasoning}`);
        }
    }

    _handleConversationRollback(evt) {
        this._inputPanel.setEnabled(true);
        this._inputPanel.focusInputField();
        this._playerSpeechMediator.hide();
        this._coachButton.setEnabled(true);
    }

    async _handleConversationStarted(evt) {
        this._nextButton.setVisible(false);

        const delay = DebugModel.fasterAnimations ? 450 : 1050;
        await PromiseUtil.delay(delay);
        await this._inputPanel.show();

        // Show onboarding step.
        this._coachButton.setEnabled(false);
        await this.showAndAwaitOnboarding(OnboardingStep.CONVERSATION_PLAYER_INPUT);
        this._coachButton.setEnabled(true);

        await PromiseUtil.delay(delay);

        // Show and animate the coach button.
        this._coachButton.skin.alpha = 0;
        this._coachButton.skin.scale = .8;
        this._coachButton.setVisible(true);
        await createjs.Tween.get(this._coachButton.skin)
            .to({
                alpha: 1,
                scale: 1,
            }, 450, createjs.Ease.backOut)
            .toPromise();
    }

    _handleConversationEnded(evt) {
        this._inputPanel.hide();

        // Hide and animate the coach button
        createjs.Tween.get(this._coachButton.skin)
            .to({
                alpha: 0,
                scale: .8,
            }, 450, createjs.Ease.backIn);
    }

    _handleDialogContent(evt) {
        /** @type {Content} */
        const content = evt.params.data.data.content;
        switch (content.getType()) {
            case ContentTypes.TYPE_CONVERSATION_TOPIC:
                this._handleConversationTopicContent(content);
                break;
            case ContentTypes.TYPE_IMAGE:
                this._handleImageContent(content);
                break;
        }
    }

    _handleCommandSequenceEnd(evt) {
        const currentOptions = this._dialogPlayer.getCurrentOptions();
        if (currentOptions.length === 0) {
            return;
        }

        this._inputPanel.hide();

        const firstOption = currentOptions[0];
        /** @type {TextContent} */
        const optionContent = DialogUtil.extractContentFromOption(firstOption);
        this._nextButton.setLabel(optionContent.getText())
        this._nextButton.setVisible(true);

        this._workingMessageOffsetForScriptedContent = 0; // Reset for next command sequence.
    }

    /**
     * Handles scripted conversation content.
     * @param {ConversationTopicContent} content 
     */
    async _handleConversationTopicContent(content) {
        const playerName = this._playerModel.getTrimmedPlayerName();

        const actionText = content.getTopicLabel().replace(/{{playerName}}/g, playerName);
        const dialogueText = content.getTopicDescription().replace(/{{playerName}}/g, playerName);
        const characterId = content.getTopicNewCondition().toLowerCase().trim();

        const characterSpeechMediator = this._characterToSpeechMediatorMap.get(characterId);
        if (!characterSpeechMediator) {
            console.error(`Character speech mediator not found for character ID: ${characterId}. Could it be that the character ID is not in chapters.json for this conversation? Skipping this dialog content...`);

            this._dialogPlayer.contentCompleted(content);
            return;
        }

        characterSpeechMediator.setAction(actionText);
        characterSpeechMediator.setSpeech(dialogueText);

        characterSpeechMediator.setOffsetPosY(this._workingMessageOffsetForScriptedContent);
        await characterSpeechMediator.showProfilePicture(0, true);
        await characterSpeechMediator.showPanels();

        const delay = DebugModel.fasterAnimations ? 0 : 650;
        await PromiseUtil.delay(delay); // Add a bit of delay before showing the next message.

        this._workingMessageOffsetForScriptedContent += ConversationScreenMediator.CHARACTER_SPEECH_OFFSET_FOR_ORDERING;

        this._dialogPlayer.contentCompleted(content);
    }

    /**
     * @param {ImageContent} content 
     */
    async _handleImageContent(content) {
        // Fade out the current background image.
        const transitionOutTweens = this._target.bgImageContainer.children.map(child => {
            return createjs.Tween.get(child)
                .to({
                    alpha: 0,
                }, 450, createjs.Ease.sineOut)
                .toPromise();
        });

        await Promise.all(transitionOutTweens);
        this._target.bgImageContainer.removeAllChildren();

        // Load the new background image.
        const imageId = content.getImage();
        const image = this._assetLibrary.getAsset(imageId);
        if (!image) {
            console.warn(`Image with id ${imageId} does not exist. Ignoring request.`);
            this._dialogPlayer.contentCompleted(content);
            return;
        }

        const bitmap = new createjs.Bitmap(image);
        DisplayObjectUtil.anchorDisplayObject(bitmap, Anchor.C);
        bitmap.alpha = 0;
        this._target.bgImageContainer.addChild(bitmap);

        // Fade in the new background image.
        await createjs.Tween.get(bitmap)
            .to({
                alpha: 1,
            }, 450, createjs.Ease.sineIn)
            .toPromise();

        this._dialogPlayer.contentCompleted(content);
    }

    async _submitPlayerInput(evt) {
        const currentText = evt.playerInput;
        if (currentText.length === 0) {
            return;
        }

        this._coachButton.setSelected(false);
        this._stopCoachButtonNudge();
        this._coachPanelMediator.hide();
        this._coachButton.setEnabled(false);

        this._inputPanel.setEnabled(false);

        this._dialogPlayer.submitPlayerInput(currentText);
        this.finishOnboarding(OnboardingStep.CONVERSATION_PLAYER_INPUT);

        this._conversationHintMediator.hide();
        await this._hideCharacterSpeechMediators();
        await PromiseUtil.delay(250);
        await this._playerSpeechMediator.showTextInput(currentText);
    }

    _handlePlayerTyping(evt) {
        this._stopCoachButtonNudge();
        this._queueCoachButtonNudge(); // Reset coach button nudge.

        this._conversationHintMediator.hide();

        this._coachPanelMediator.hide();
        this._coachButton.setSelected(false);
    }

    _handleBackButton() {
        throw new Error("Method not implemented.");
    }

    async _handleNextButton() {
        this._nextButton.setVisible(false);
        await this._hideCharacterSpeechMediators();
        await PromiseUtil.delay(150); // Add a bit of delay before showing the next message.

        const currentOptions = this._dialogPlayer.getCurrentOptions();
        const firstOption = currentOptions[0];
        this._dialogPlayer.optionChosen(firstOption);
    }

    _handleEndButton() {
        const conversation = this._dialogPlayer.llmConversationPlayer.conversation;
        const evt = new ConversationFinishedEvent(ConversationFinishedEvent.PERFORM, conversation);
        this.dispatch(evt.type, { data: evt });
    }

    _handleCoachButton() {
        this._stopCoachButtonNudge();

        this._coachButton.setSelected(!this._coachButton.getSelected());
        this._coachPanelMediator.toggle();
        this._conversationHintMediator.hide();

        this.finishOnboarding(OnboardingStep.CONVERSATION_HAND_OFF);
    }

    async _hideCharacterSpeechMediators() {
        const promises = [];
        for (const characterSpeechMediator of this._characterSpeechMediators) {
            const promise = characterSpeechMediator.hideAll();
            promises.push(promise);
        }

        return Promise.all(promises);
    }

    _handleBackgroundClicked() {
        this._coachPanelMediator.hide();
        this._coachButton.setSelected(false);
    }

    async _start() {
        // Hide everything first.
        this._nextButton.setVisible(false);

        for (const characterSpeechMediator of this._characterSpeechMediators) {
            characterSpeechMediator.hideAllInstant();
        }

        this._dialogPlayer.viewContent();
    }

    _showDebug() {
        // Draw panel behind vertical layout.
        const layoutPanel = new createjs.Shape();
        layoutPanel.graphics.beginFill("#000").drawRect(-1920 * 0.5, -1080 * 0.5, 300, 100);
        layoutPanel.alpha = .5;
        this._target.addChild(layoutPanel);

        // Vertical layout elements.
        this._debugTextContainer = new createjs.Container();
        this._debugTextContainer.x = -1920 * 0.5 + 10;
        this._debugTextContainer.y = -1080 * 0.5 + 10;
        this._target.addChild(this._debugTextContainer);

        this._debugTextContainerVerticalLayout = new VerticalLayout(this._debugTextContainer, 10)

        this._debugPhaseTextField = new createjs.Text(`Phase: SCRIPTED`, 'bold 24px Arial', '#F00');
        this._debugTextContainer.addChild(this._debugPhaseTextField);

        this._debugOutcomeTextField = new createjs.Text(`Outcome: Unknown`, 'bold 24px Arial', '#F00');
        this._debugTextContainer.addChild(this._debugOutcomeTextField);

        this._debugTextContainerVerticalLayout.repositionItems();

        // Draw panel behind phase reasoning field.
        const phaseReasoningPanel = new createjs.Shape();
        phaseReasoningPanel.graphics.beginFill("#000").drawRect(-1920 * 0.5, 100, 1920, 200);
        phaseReasoningPanel.alpha = .5;
        this._target.addChild(phaseReasoningPanel);

        // Outside of the vertical layout.
        this._debugPhaseReasoningTextField = new createjs.Text(``, 'bold 24px Arial', '#F00');
        this._debugPhaseReasoningTextField.x = -1920 * 0.5 + 100;
        this._debugPhaseReasoningTextField.y = 100;
        this._debugPhaseReasoningTextField.lineWidth = 1920 - 200;
        this._debugPhaseReasoningTextField.lineHeight = 30;
        this._target.addChild(this._debugPhaseReasoningTextField);
    }

    _enablePlayerInput() {
        // Re-enable player UI input elements.
        this._inputPanel.setEnabled(true);
        this._inputPanel.focusInputField();
        this._coachButton.setEnabled(true);
    }

    _queueCoachButtonNudge() {
        this._cancelCoachButtonNudgeQueue();

        const nudgeAfterMs = this._config.getValue(`onboarding.coachNudgeDelay`);
        this._coachNudgeTimeout = setTimeout(this._showCoachButtonNudge.bind(this), nudgeAfterMs);
    }

    _cancelCoachButtonNudgeQueue() {
        if (!this._coachNudgeTimeout) {
            return;
        }

        clearTimeout(this._coachNudgeTimeout);
        this._coachNudgeTimeout = null;
    }

    _showCoachButtonNudge() {
        this._soundManager.playSound(SoundType.COACH_BUTTON_NUDGE);
        this._coachButton.setAttract(true);
    }

    _stopCoachButtonNudge() {
        this._coachButton.setAttract(false);
        this._cancelCoachButtonNudgeQueue();
    }

    /**
     * Plays audio at the start of each attract animation.
     * @param {Button} button 
     * @param {string} soundId SoundType
     */
    _attachAudioToAttractState(button, soundId) {
        const buttonSkin = button.skin;
        buttonSkin.attract.timeline.addEventListener("change", () => {
            if (!button.getAttract()) {
                return;
            }

            if (buttonSkin.attract.currentFrame === 1) {
                if (buttonSkin.attract.soundRef) {
                    this._soundManager.stopSound(buttonSkin.attract.soundRef);
                    delete buttonSkin.attract.soundRef;
                }

                const soundRef = this._soundManager.playSound(soundId);
                buttonSkin.attract.soundRef = soundRef;
            }
        });
    }
}

ConversationScreenMediator.CHARACTER_SPEECH_OFFSET_FOR_ORDERING = 100;
ConversationScreenMediator.CHARACTER_SPEECH_STAGGER_DELAY_MS = 600;